import {DateTime} from 'luxon';

export interface DateTimeUnitsObject {
  years?: number | undefined;
  quarters?: number | undefined;
  months?: number | undefined;
  weeks?: number | undefined;
  days?: number | undefined;
  hours?: number | undefined;
  minutes?: number | undefined;
  seconds?: number | undefined;
  milliseconds?: number | undefined;
}

export type DateTimeUnit = keyof DateTimeUnitsObject;

function isString(val: string | Date | null): val is string {
  return typeof val === 'string';
}

export class DateService {
  /**
   * Parses strings formatted to the ISO 8601 standard and returns the corresponding date time in the client's
   * time-zone.
   * @param src an ISO8601 formatted string
   */
  static fromIso(src: string): Date {
    const result = DateTime.fromISO(src);
    if (!result.isValid) {
      throw new Error(
        result.invalidExplanation
          ? result.invalidExplanation
          : 'Invalid ISO Date',
      );
    }

    return result.toJSDate();
  }

  /**
   * Returns the date time for the current instant.
   */
  static now(): Date {
    return DateTime.now().toJSDate();
  }

  /**
   * Returns the difference between two dates in the provided units.
   * @param d1 First Date for comparison.
   * @param d2 Second Date for comparison.
   * @param units Units (DateTimeUnit) for the calculated value.
   */
  static diff(d1: Date, d2: Date, units: DateTimeUnit): number {
    const dt1 = DateTime.fromJSDate(d1);
    const dt2 = DateTime.fromJSDate(d2);

    return dt1.diff(dt2, units).as(units).valueOf();
  }

  /**
   * Subtracts a given measurement of time from a Date.
   * @param date Date from which to subtract the given value and unit.
   * @param units DateTimeUnitsObject contains value and units which are subtracted from the provided Date.
   */
  static subtract(date: Date, units: DateTimeUnitsObject): Date {
    return DateTime.fromJSDate(date).minus(units).toJSDate();
  }

  /**
   * Adds a give measurement of time to a Date.
   * @param date Date to add to.
   * @param units DateTimeUnitsObject value and units to add to the provided Date.
   */
  static add(date: Date, units: DateTimeUnitsObject): Date {
    return DateTime.fromJSDate(date).plus(units).toJSDate();
  }

  /**
   * Returns the current year.
   */
  static year(): number {
    return DateTime.now().year;
  }

  /**
   * Returns the today's date with the time at the end of the day, or if provided a value will calculate the end based
   * off of that date in the client's time-zone.
   * @param date Date; Optional date which to calculate the end of.
   */
  static endOfDay(date?: Date): Date {
    const dt = date ? DateTime.fromJSDate(date) : DateTime.now();
    return dt.endOf('day').toJSDate();
  }

  /**
   * Returns the date with the time at 12AM in the client's time-zone. If provided with a date the result will be the
   * start of the provided date in the client's time-zone.
   * @param date Date; Optional date for which to calculate the start of.
   */
  static startOfDay(date?: Date): Date {
    const dt = date ? DateTime.fromJSDate(date) : DateTime.now();
    return dt.startOf('day').toJSDate();
  }

  /***
   * Parses a date string using the given format, and returns the value as a Date. The format uses Luxon's tokens.
   * @param value the date string to be parsed
   * @param format the format of date being parsed using luxon tokens
   */
  static fromFormat(value: string, format: string): Date {
    return DateTime.fromFormat(value, format).toJSDate();
  }

  /**
   * Specifically added to support the zod schema for product parameters.
   * @param value the value that is being parsed
   * @param format the date format using luxon tokens; defaults to dd/MM/yyyy
   */
  static parseContextDate(
    value: string | Date | null,
    format = 'MM/dd/yyyy',
  ): Date | null {
    if (!value) return null;

    let result: Date | null = null;

    if (isString(value)) {
      let dateTime = DateTime.fromFormat(value, format);
      if (!dateTime || !dateTime.isValid) {
        dateTime = DateTime.fromISO(value);
      }
      result = dateTime.toJSDate();
    } else {
      result = value;
    }

    return result;
  }

  static isoDate(date: Date): string | null {
    return DateTime.fromJSDate(date).toISODate();
  }

  /**
   * Compares two Date objects and returns true if they are equal including the time stamp.
   * @param d1 the first Date being compared
   * @param d2 the second Date being compared
   */
  static datesAreEqual(
    d1: Date | null | undefined,
    d2: Date | null | undefined,
  ): boolean {
    return d1 === d2 || (!!d1 && !!d2 && !(d1 > d2 || d1 < d2));
  }
}
